import React, { useState, useRef, useEffect } from 'react';
import { Form, Button } from 'react-bootstrap';
import { AlignLeft, Edit2, Check, X } from 'lucide-react';
import { ProjectModel } from '../../../types/projects';

interface ProjectDescriptionSectionProps {
    project: ProjectModel;
    onUpdateDescription: (description: string) => Promise<void>;
    submitting?: boolean;
}

export const ProjectDescriptionSection: React.FC<ProjectDescriptionSectionProps> = ({
    project,
    onUpdateDescription,
    submitting = false,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);
    const inputRef = useRef<HTMLTextAreaElement>(null);

    // Focus the textarea when entering edit mode
    useEffect(() => {
        if (isEditing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [isEditing]);

    const handleEdit = () => {
        setValue(project.description ?? '');
        setError(null);
        setIsEditing(true);
    };

    const handleSave = async () => {
        setSaving(true);
        setError(null);
        try {
            await onUpdateDescription(value);
            setIsEditing(false);
        } catch (err) {
            console.error('Error saving description:', err);
            setError('Kunde inte spara beskrivning');
        } finally {
            setSaving(false);
        }
    };

    const handleCancel = () => {
        setValue('');
        setError(null);
        setIsEditing(false);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Escape') {
            handleCancel();
        }
    };

    const labelStyle: React.CSSProperties = {
        display: 'flex', alignItems: 'center', gap: '6px',
        color: '#000', fontSize: '14px', fontWeight: 700, marginBottom: '1px',
    };
    const valueIndent: React.CSSProperties = { paddingLeft: '20px' };

    if (isEditing) {
        return (
            <div style={{ marginBottom: '24px' }}>
                <div style={labelStyle}>
                    <AlignLeft size={14} />
                    Beskrivning:
                </div>
                <div style={{ ...valueIndent, marginTop: '4px' }}>
                    <Form.Control
                        as="textarea"
                        ref={inputRef}
                        value={value}
                        onChange={(e) => setValue(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Lägg till en beskrivning..."
                        rows={4}
                        disabled={saving || submitting}
                        style={{ maxWidth: '480px' }}
                    />
                    {error && (
                        <div style={{ color: '#dc3545', fontSize: '13px', marginTop: '6px' }}>{error}</div>
                    )}
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                        <Button size="sm" variant="success" onClick={handleSave} disabled={saving || submitting}>
                            <Check size={16} />
                        </Button>
                        <Button size="sm" variant="secondary" onClick={handleCancel} disabled={saving || submitting}>
                            <X size={16} />
                        </Button>
                    </div>
                </div>
            </div>
        );
    }

    // Display mode
    const hasDescription = !!project.description?.trim();
    return (
        <div style={{ marginBottom: '24px' }}>
            <div style={labelStyle}>
                <AlignLeft size={14} />
                Beskrivning:
            </div>
            <div style={{ ...valueIndent, display: 'flex', alignItems: 'flex-start', gap: '8px' }}>
                {hasDescription ? (
                    <span style={{ fontSize: '16px', whiteSpace: 'pre-wrap' }}>{project.description}</span>
                ) : (
                    <span style={{ fontSize: '16px', color: '#999', fontStyle: 'italic' }}>Ej angiven</span>
                )}
                {!submitting && (
                    <Button size="sm" variant="link" onClick={handleEdit} style={{ padding: '2px 6px', color: '#666', flexShrink: 0 }}>
                        <Edit2 size={14} />
                    </Button>
                )}
            </div>
        </div>
    );
};
